<script lang="ts">
  import type { LeaderboardSkillsDetail, LeaderboardRank } from '$lib/api/types';
  import { auth } from '$lib/stores/auth.svelte';
  import type { OptInOutDirection } from '$lib/componentTypes';
  import OptInOutConfirmModal from './OptInOutConfirmModal.svelte';
  import { isClassOpen } from '$lib/util';
  import TableMessage from '../TableMessage.svelte';
  import Podium from './Podium.svelte';
  import Top10Table from './Top10Table.svelte';

  interface Props {
    rankings: LeaderboardRank[];
    studentClassInfo: LeaderboardSkillsDetail;
    isLoading: boolean;
    updatedTime: string | null;
  }

  let { rankings, studentClassInfo, isLoading, updatedTime }: Props = $props();

  let optInOut: OptInOutDirection = $state(null);

  let thisUsersRank =
    studentClassInfo.isOptedIn && rankings.length > 0
      ? rankings.find(x => x.studentId === auth.user!.userId)?.rank
      : undefined;

  const classOpen = isClassOpen(studentClassInfo.class);
</script>

<!-- header -->
<div class="d-flex align-items-center">
  <h3 class="mb-0 flex-grow-1">Leaderboard</h3>
  {#if classOpen}
    {#if studentClassInfo.isOptedIn}
      <button class="btn btn-link p-0" onclick={() => (optInOut = 'out')}>Opt out</button>
    {:else}
      <button class="btn btn-link p-0" onclick={() => (optInOut = 'in')}>Opt in</button>
    {/if}
  {/if}
</div>
<div class="small fst-italic text-secondary mb-3">
  {#if isLoading}
    Updating...
  {:else}
    Updated {updatedTime}
  {/if}
</div>

<!-- podium -->
{#if rankings.length > 0}
  <Podium
    firstPlace={rankings[0]}
    secondPlace={rankings.length > 1 && rankings[1]}
    thirdPlace={rankings.length > 2 && rankings[2]}
  />
{:else if studentClassInfo.isOptedIn && classOpen}
  <TableMessage
    message="Leaderboard is empty! Log skills and earn the first spot on the class leaderboard."
    _class="my-5"
  />
{:else}
  <TableMessage message="Leaderboard is empty!" _class="my-5" />
{/if}

{#if rankings.length === 0 && classOpen}
  <div class="d-grid my-5">
    <a href="/classes/{studentClassInfo.classId}/log" class="btn btn-primary">Log Skills</a>
  </div>
{/if}

<!-- next 7 rank table -->
{#if rankings.length >= 4}
  <Top10Table leaderboard={rankings.slice(3)} />
{/if}

<!-- if student is opted in but has no rank because score is zero, invite to log skills -->
{#if classOpen && studentClassInfo.isOptedIn && thisUsersRank === undefined}
  <p class="border-top pt-2">
    <a href="/classes/{studentClassInfo.classId}/log">Log your first skill</a> to see your rank in the class leaderboard.
  </p>
{/if}

<!-- opt in invitation -->
{#if rankings.length > 0 && !studentClassInfo.isOptedIn}
  <p class="border-top pt-2">
    Compete with the rest of your class by
    <!-- need to style as link but behave as a button -->
    <!-- svelte-ignore a11y_invalid_attribute -->
    <a
      href="#"
      role="button"
      onclick={e => {
        e.preventDefault();
        optInOut = 'in';
      }}>opting into the class leaderboard</a
    >!
  </p>
{/if}

{#if optInOut !== null}
  <OptInOutConfirmModal classId={studentClassInfo.classId} bind:optIn={optInOut} />
{/if}
